import { ORDER_STATUSES, ORDER_STATUS_LABEL, type Order, type OrderStatus } from '@nsd/core';
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ErrorBox, Loading, Money, Status, api, useLoad, when } from '../ui.tsx';

/** Order rows: a card per order on phones, a table-like grid on wide screens. Used by several pages. */
export function OrderList({ orders }: { orders: Order[] }) {
  if (!orders.length) return <p className="muted">No orders.</p>;
  return (
    <ul className="olist">
      {orders.map((o) => (
        <li key={o.number}>
          <Link to={`/orders/${o.number}`} className="orow">
            <span className="orow__num">#{o.number}</span>
            <span className="orow__who">
              {o.shippingAddress.firstName} {o.shippingAddress.lastName}
              <small>{o.shippingAddress.city}, {o.shippingAddress.province}</small>
            </span>
            <span className="orow__when">{when(o.createdAt)}</span>
            <span className="orow__qty">{o.items.reduce((n, i) => n + i.qty, 0)} ctn</span>
            <span className="orow__total"><Money cents={o.totalCents} /></span>
            <span className="orow__status">
              <Status status={o.status} />
              {o.status === 'pending_payment' && o.etransfer.answer && <span className="pill pill--ok">Answer in</span>}
            </span>
          </Link>
        </li>
      ))}
    </ul>
  );
}

export function Orders() {
  const [params, setParams] = useSearchParams();
  const status = (params.get('status') || undefined) as OrderStatus | undefined;
  const [pages, setPages] = useState<Order[][]>([]);
  const [cursor, setCursor] = useState<string | undefined>();
  const { error, loading } = useLoad(async () => {
    const r = await api.orders({ status });
    setPages([r.orders]);
    setCursor(r.cursor);
    return r;
  }, [status]);
  const more = async () => {
    const r = await api.orders({ status, cursor });
    setPages((p) => [...p, r.orders]);
    setCursor(r.cursor);
  };
  return (
    <>
      <h1>Orders</h1>
      <div className="chips">
        <button className={`chip ${!status ? 'is-on' : ''}`} onClick={() => setParams({})}>All</button>
        {ORDER_STATUSES.map((s) => (
          <button key={s} className={`chip ${status === s ? 'is-on' : ''}`} onClick={() => setParams({ status: s })}>
            {ORDER_STATUS_LABEL[s]}
          </button>
        ))}
      </div>
      <ErrorBox error={error} />
      {loading && !pages.length ? <Loading /> : <OrderList orders={pages.flat()} />}
      {cursor && (
        <button className="btn btn--ghost" onClick={() => void more()}>
          Load more
        </button>
      )}
    </>
  );
}
